import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, Loader2, PencilLine, Eraser, ArrowRight } from "lucide-react";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

/** Central check-in flow — guest arrives, scans reception QR, enters email,
 * signs waiver (if needed), then picks a booking. The QR carries a
 * display_token + rotating `token` in the URL. */
export default function Checkin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const displayToken = params.get("display_token") || "";
  const preToken = params.get("token") || "";

  const [step, setStep] = useState("email"); // email | sign | bookings | done
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [waiver, setWaiver] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);

  const fetchRotatingToken = async () => {
    if (preToken) return preToken;
    const r = await apiClient.get("/qr/central/token", { params: { display_token: displayToken } });
    return r.data.token;
  };

  const submitEmail = async (e) => {
    e?.preventDefault();
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try {
      const token = await fetchRotatingToken();
      const r = await apiClient.post("/checkin/central", { token, email });
      setBookings(r.data.bookings || []);
      if (r.data.has_valid_signature) {
        if ((r.data.bookings || []).length === 0) {
          setStep("no-booking");
        } else {
          setStep("bookings");
        }
      } else {
        // Need to sign
        const w = await apiClient.get("/waiver/current");
        setWaiver(w.data);
        setStep("sign");
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Check-in failed"));
    } finally { setBusy(false); }
  };

  const submitSignature = async () => {
    if (!fullName.trim()) return toast.error("Full name is required");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please sign in the box");
    setBusy(true);
    try {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await apiClient.post("/waiver/sign", {
        version_id: waiver.id,
        full_name: fullName,
        email,
        signature_data_url: dataUrl,
        phone: phone || null,
      });
      // refresh bookings after signing (guest slots may now attach)
      const token = await fetchRotatingToken();
      const r = await apiClient.post("/checkin/central", { token, email });
      setBookings(r.data.bookings || []);
      if ((r.data.bookings || []).length === 0) setStep("no-booking");
      else setStep("bookings");
    } catch (e) {
      toast.error(getErrorMessage(e, "Signing failed"));
    } finally { setBusy(false); }
  };

  const pickBooking = (b) => {
    setStep("done");
    setTimeout(() => {
      // Nothing else — the guest is checked in. Reset for the next arrival.
      setEmail(""); setFullName(""); setPhone(""); setBookings([]);
      setStep("email");
    }, 6000);
  };

  const fmtWhen = (b) => {
    const s = new Date(b.start_time), e = new Date(b.end_time);
    const tstr = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${s.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} · ${tstr(s)} – ${tstr(e)}`;
  };

  return (
    <div className="min-h-screen grid-overlay flex flex-col items-center justify-center px-4 py-6">
      <div className="w-full max-w-lg glass rounded-3xl p-8">
        <img src={logo} alt="Pit Lane" className="h-12 w-auto object-contain mx-auto mb-6" />

        {step === "email" && (
          <>
            <h1 className="font-display text-3xl font-bold text-white text-center mb-2">Welcome to Pit Lane</h1>
            <p className="text-slate-400 text-center mb-6">Enter your email to check in.</p>
            <form onSubmit={submitEmail} className="space-y-3">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="checkin-email-input"
                placeholder="you@email.com"
                className="input-cyber w-full text-lg" required autoFocus />
              <button type="submit" disabled={busy} className="neon-btn w-full py-4" data-testid="checkin-submit-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <><span>Continue</span> <ArrowRight className="w-5 h-5 ml-2 inline" /></>}
              </button>
            </form>
          </>
        )}

        {step === "sign" && waiver && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">{waiver.title}</h1>
            <p className="text-slate-500 text-xs mb-3">v{waiver.version}</p>
            <div className="max-h-56 overflow-y-auto p-4 rounded-xl bg-black/40 border border-white/10 text-slate-200 text-sm mb-4 prose prose-invert prose-sm max-w-none"
                 dangerouslySetInnerHTML={{ __html: waiver.body_html }} data-testid="waiver-body" />
            <div className="space-y-2">
              <input type="text" placeholder="Full legal name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                data-testid="waiver-name-input" className="input-cyber w-full" required />
              <input type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
                data-testid="waiver-phone-input" className="input-cyber w-full" />
            </div>
            <div className="mt-3">
              <div className="text-slate-400 text-xs mb-1 flex items-center gap-1"><PencilLine className="w-3 h-3" /> Sign below</div>
              <div className="bg-white rounded-xl">
                <SignatureCanvas ref={sigRef} penColor="black"
                  canvasProps={{ width: 460, height: 160, className: "w-full rounded-xl", "data-testid": "waiver-signature-canvas" }} />
              </div>
              <button onClick={() => sigRef.current?.clear()} className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid="waiver-clear-btn">
                <Eraser className="w-3 h-3" /> Clear signature
              </button>
            </div>
            <button onClick={submitSignature} disabled={busy} className="neon-btn w-full py-4 mt-4" data-testid="waiver-submit-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Agree & Check In"}
            </button>
          </>
        )}

        {step === "bookings" && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">Pick your booking</h1>
            <p className="text-slate-400 mb-4 text-sm">Hi {email} — we found these active bookings:</p>
            <div className="space-y-2">
              {bookings.map((b) => (
                <button key={b.id} onClick={() => pickBooking(b)}
                  data-testid={`checkin-booking-${b.id}`}
                  className="w-full text-left p-4 rounded-xl bg-black/30 border border-white/10 hover:border-pink-500/50 hover:bg-pink-500/5 transition">
                  <div className="text-white font-semibold">{(b.stations || []).map((s) => s.name).join(" + ") || "Booking"}</div>
                  <div className="text-slate-400 text-sm">{fmtWhen(b)}</div>
                </button>
              ))}
            </div>
            <button onClick={() => setStep("email")} className="neon-outline-btn w-full py-3 mt-4" data-testid="checkin-restart-btn">Start over</button>
          </>
        )}

        {step === "no-booking" && (
          <>
            <h1 className="font-display text-2xl font-bold text-white mb-2">You're all set</h1>
            <p className="text-slate-400 mb-4">Waiver on file — please see the front desk to book a session.</p>
            <button onClick={() => setStep("email")} className="neon-outline-btn w-full py-3" data-testid="checkin-back-btn">Back to start</button>
          </>
        )}

        {step === "done" && (
          <div className="text-center py-8">
            <CheckCircle2 className="w-20 h-20 mx-auto text-emerald-400 mb-4" data-testid="checkin-done-icon" />
            <h1 className="font-display text-3xl font-bold text-white mb-2">You're checked in!</h1>
            <p className="text-slate-400">Head to your station and scan the station QR to begin.</p>
          </div>
        )}
      </div>
    </div>
  );
}
